import { EventEmitter, Injectable } from '@angular/core';
import { Observable, Subscription, combineLatest, of } from 'rxjs';
import { OrderService } from './order.service';
import {
  OrderDisplayData,
  OrderLine,
  PackageGroup,
  ShareGroup,
} from '../model/interfaces/order-display-data';
import { Order } from 'src/app/model/interfaces/order';
import { ReceiptService } from './receipt-service';
import { map, switchMap } from 'rxjs/operators';
import { TicketService } from './ticket.service';
import { PriceService } from './price.service';
import { TicketPackageInstance } from '../model/interfaces/ticket-package-instance';
import { ResourceService } from './resource.service';
import { Share } from '../model/interfaces/share';
import { LotteryService } from './lottery.service';
import { PaymentModel } from '../model/enums/payment-model';
import { CheckoutState } from '../model/enums/checkout-state';

@Injectable()
export class OrderDisplayDataService {
  private onOrderUpdatedSubscription = new Subscription();
  private onOrderExpiredSubscription = new Subscription();
  private onOrderExpirationNoticeSubscription = new Subscription();

  private readonly orderService: OrderService;
  private readonly receiptService: ReceiptService;
  private readonly ticketService: TicketService;
  private readonly priceService: PriceService;
  private readonly resourceService: ResourceService;
  private readonly lotteryService: LotteryService;

  private priceSubscription = new Subscription();

  private order: Order;
  private orderDisplayData: OrderDisplayData;
  public paymentModel: PaymentModel = PaymentModel.Subscription;
  public isBsAppActive: boolean;
  public checkoutState: CheckoutState = CheckoutState.Payment;

  public onOrderDisplayDataUpdated = new EventEmitter<OrderDisplayData>();

  public constructor(
    orderService: OrderService,
    receiptService: ReceiptService,
    ticketService: TicketService,
    priceService: PriceService,
    resourceService: ResourceService,
    lotteryService: LotteryService
  ) {
    this.orderService = orderService;
    this.receiptService = receiptService;
    this.ticketService = ticketService;
    this.priceService = priceService;
    this.resourceService = resourceService;
    this.lotteryService = lotteryService;
    this.orderDisplayData = {} as OrderDisplayData;
    this.checkoutState = CheckoutState.Payment;
    this.setupSubscriptions();
    this.update();
  }

  public setProperties(
    paymentModel: PaymentModel,
    isBsAppActive: boolean,
    checkoutState: CheckoutState
  ): void {
    this.paymentModel = paymentModel;
    this.isBsAppActive = isBsAppActive;
    this.checkoutState = checkoutState;
    this.update();
  }

  public async getOrderDisplayData(): Promise<OrderDisplayData> {
    return this.update().then(() => this.orderDisplayData);
  }
  private setupSubscriptions(): void {
    this.onOrderUpdatedSubscription = this.orderService.onOrderContentUpdated.subscribe(() =>
      this.onOrderContentUpdated()
    );
    this.onOrderExpiredSubscription = this.orderService.onOrderExpired.subscribe(() =>
      this.terminate()
    );
    this.onOrderExpirationNoticeSubscription = this.orderService.onOrderExpirationNotice.subscribe(
      () => this.warnTermination()
    );
  }

  private terminate(): void {
    this.order = null;
    this.onOrderDisplayDataUpdated.emit(null);
  }

  private warnTermination(): void {
    console.log('terminates in a short bit');
  }

  private onOrderContentUpdated(): void {
    this.update();
  }

  private async update(): Promise<void> {
    const order = await this.getOrder();
    if (order != null) {
      this.order = order;
      this.orderDisplayData.order = order;
      this.buildModel();
    }
  }

  private async getOrder(): Promise<Order> {
    if (this.checkoutState === CheckoutState.Payment) {
      return this.receiptService.getOrders().orderSubscription;
    }

    return this.orderService.getOrder(false);
  }

  private buildModel(): void {
    // Cancel any in-flight price calculation from a previous build so a late-resolving
    // stale observable can't overwrite the display data we're about to compute here.
    this.priceSubscription.unsubscribe();

    const order = this.order;
    const shares = order.Shares;
    const packageInstances = order.TicketPackageInstances;
    switch (this.paymentModel) {
      case PaymentModel.OneTime:
        this.orderDisplayData.classCount = 1;
        break;
      default:
        this.orderDisplayData.classCount = order.NumberOfClasses;
        break;
    }
    if (shares.length === 0 && packageInstances.length === 0) {
      this.orderDisplayData.orderLines = [];
      this.orderDisplayData.packageOrderLines = [];
      this.orderDisplayData.shareOrderLines = [];
      this.orderDisplayData.itemCount = 0;
      this.orderDisplayData.priceTotal = 0;
      this.orderDisplayData.pricePrMonth = 0;
      this.orderDisplayData.topPrize = 0;
      this.orderDisplayData.minPrize = 0;
      this.orderDisplayData.fees = [];
      // Emit so subscribers (basket page/orderline list) reflect the now-empty
      // basket immediately instead of only after a page reload.
      this.onOrderDisplayDataUpdated.emit(this.orderDisplayData);
      return;
    }

    this.getDrawDates().subscribe((drawDates) => {
      this.orderDisplayData.drawDateFirst = drawDates.drawDateFirst;
      this.orderDisplayData.drawDateLast = drawDates.drawDateLast;

      this.priceSubscription = combineLatest([
        this.getPackageOrderLines(order.TicketPackageInstances),
        this.getShareOrderLines(order.Shares),
      ])
        .pipe(
          switchMap((args: [OrderLine[], OrderLine[]]) => {
            const [packageOrderLines, shareOrderLines]: [OrderLine[], OrderLine[]] = args;
            this.orderDisplayData.packageOrderLines = packageOrderLines as any as PackageGroup[];
            this.orderDisplayData.shareOrderLines = shareOrderLines as any as ShareGroup[];
            this.orderDisplayData.orderLines = [...packageOrderLines, ...shareOrderLines];

            this.orderDisplayData.itemCount = this.orderDisplayData.orderLines.reduce(
              (accumulator: number, orderLine: OrderLine) => accumulator + orderLine.items.length,
              0
            );

            this.orderDisplayData.winChance = this.ticketService.getWinChanceByOrder(order);

            return this.priceService.getPriceByOrder(this.order);
          })
        )
        .subscribe((priceData) => {
          this.orderDisplayData.pricePrMonth = priceData.pricePrMonth;
          // TODO: Refactor this price calculation bit.
          this.orderDisplayData.priceTotal = this.orderDisplayData.orderLines.reduce<number>(
            (price: number, orderline: OrderLine) => {
              price +=
                this.orderDisplayData.classCount === 6
                  ? orderline.price.subscriptionSixMonths / 6
                  : orderline.price.subscriptionSingleMonth;
              return price;
            },
            0
          );

          // TODO: Refactor
          this.orderDisplayData.fees =
            this.paymentModel === PaymentModel.Subscription ? priceData.fees : [];

          this.orderDisplayData.topPrize = this.orderDisplayData.orderLines.reduce<number>(
            (topPrize: number, orderline: OrderLine) => {
              return Math.max(orderline.topPrize, topPrize);
            },
            0
          );

          this.orderDisplayData.minPrize = this.orderDisplayData.orderLines.reduce<number>(
            (minPrize: number, orderline: OrderLine) => {
              return Math.min(minPrize == 0 ? Infinity : minPrize, orderline.minPrize); // filter out null/0 values by using Infinity
            },
            0
          );

          this.orderDisplayData.priceChangeInEffect =
            this.priceService.getPriceChangeInEffectStatus(priceData);

          this.onOrderDisplayDataUpdated.emit(this.orderDisplayData);
        });
    });
  }

  private getDrawDates(): Observable<{ drawDateFirst: Date; drawDateLast: Date }> {
    // this.order.SubscriptionStartClassId The id of the class for upcoming betalingsservice
    // this.order.FMTClassId The id of the class for upcoming credit card payment
    let paymentClassId = -1;
    if (this.paymentModel === PaymentModel.Subscription && !this.isBsAppActive) {
      // If betalingsservice
      paymentClassId = this.order.SubscriptionStartClassId;
    } else {
      // If dankort
      paymentClassId = this.order.FMTClassId;
    }

    // First class of game
    return this.lotteryService.getLotteryClassById(paymentClassId).pipe(
      map((salesClass) => {
        // Sets the 1st date of the game period.
        const drawDateFirst = new Date(salesClass.Draws[0].DrawDate);

        // Gets the 19th of the last draw in the game period.
        let drawDateLast = null;

        if (this.isBsAppActive) {
          drawDateLast = new Date(
            drawDateFirst.getFullYear(),
            drawDateFirst.getMonth() + (this.orderDisplayData.classCount === 6 ? 6 : 1),
            drawDateFirst.getDate() + 4
          );
        } else {
          drawDateLast = new Date(
            drawDateFirst.getFullYear(),
            drawDateFirst.getMonth() + (this.orderDisplayData.classCount === 6 ? 5 : 0),
            drawDateFirst.getDate() + 4
          );
        }
        return {
          drawDateFirst,
          drawDateLast,
        };
      })
    );
  }

  private getPackageOrderLines(
    packageInstances: ReadonlyArray<TicketPackageInstance>
  ): Observable<OrderLine[]> {
    if (packageInstances.length === 0) {
      return of([]);
    }

    const packageGroups = packageInstances.reduce<{ [key: string]: PackageGroup }>(
      (groups: { [key: string]: PackageGroup }, packageInstance: TicketPackageInstance) => {
        const packageId = packageInstance.TicketPackage.Id;
        (
          groups[packageId] || (groups[packageId] = this.getPackageGroup(packageInstance))
        ).packageInstances.push(packageInstance);
        return groups;
      },
      {}
    );

    const tasks = [];

    Object.keys(packageGroups).map((key) => {
      const group = packageGroups[key];
      const groupName = group.name;
      const shareGroupList = Object.keys(group.shareGroups).map((key) => group.shareGroups[key]);

      const sortingDirection = -1;

      shareGroupList.sort((a: ShareGroup, b: ShareGroup) => {
        return (a.size - b.size) * sortingDirection;
      });

      // Calculates the price of six months for all selected package instances.
      tasks.push(
        this.priceService.getPriceByPackage(group.packageInstances[0].TicketPackage).pipe(
          map((p) => {
            let priceFactor = group.packageInstances.length;

            p.fmt.ticketPrice *= priceFactor;
            p.subscriptionSingleMonth *= priceFactor;
            p.subscriptionSixMonths *= priceFactor;
            p.subscriptionPriceList.forEach((price) => {
              price.ticketPrice *= priceFactor;
            });

            const winChanceTicketPackage = this.ticketService.getWinChanceByPackageContent(
              group.packageInstances[0].TicketPackage
            );

            const orderLine = {
              items: group.packageInstances,
              name: groupName,
              price: p,
              shareGroupList,
              winChance: winChanceTicketPackage,
              iconUrl: this.resourceService.getString(`shop_package_image_url_${groupName}`),
              topPrize: parseInt(
                this.resourceService.getString(`global_package_topprize_${groupName}`),
                10
              ),
              minPrize: parseInt(
                this.resourceService.getString(`global_package_min_prize_${groupName}`),
                10
              ),
            };
            return orderLine;
          })
        )
      );
    });

    return combineLatest<OrderLine[]>(tasks);
  }

  private getShareOrderLines(shares: ReadonlyArray<Share>): Observable<OrderLine[]> {
    if (shares.length === 0) {
      return of([]);
    }

    const shareGroups = this.getShareGroups(shares);

    const tasks = [];

    Object.keys(shareGroups).map((key) => {
      const group = shareGroups[key];
      const groupName = group.name;

      tasks.push(
        this.priceService.getPriceByShareType(group.shares[0].Type).pipe(
          map((p) => {
            const priceFactor = group.shares.length;
            p.fmt.ticketPrice *= priceFactor;
            p.subscriptionSingleMonth *= priceFactor;
            p.subscriptionSixMonths *= priceFactor;
            p.subscriptionPriceList.forEach((price) => {
              price.ticketPrice *= priceFactor;
            });

            return {
              name: groupName,
              items: this.getShareGroupItems(group.shares),
              price: p,
              iconUrl: this.resourceService.getString(
                `global_ticket_image_url_simple_vert_${groupName}`
              ),
              topPrize: parseInt(
                this.resourceService.getString('global_share_type_top_prize_' + groupName),
                10
              ),
              minPrize: parseInt(
                this.resourceService.getString('global_share_type_min_prize_' + groupName),
                10
              ),
            };
          })
        )
      );
    });

    return combineLatest<OrderLine[]>(tasks);
  }

  private getShareGroupItems(shares: Share[]): any[] {
    return shares.map((share) => {
      return { share, isDreamTicket: this.orderService.isDreamTicket(share) };
    });
  }

  private getShareGroups(shares: ReadonlyArray<Share>): { [key: string]: ShareGroup } {
    return shares.reduce<{ [key: string]: ShareGroup }>(
      (groups: { [key: string]: ShareGroup }, share: Share) => {
        (groups[share.Type.Id] || (groups[share.Type.Id] = this.getShareGroup(share))).shares.push(
          share
        );
        return groups;
      },
      {}
    );
  }

  private getShareGroup(share: Share): ShareGroup {
    return {
      shares: [],
      name: share.Type.Name,
      size: share.Type.Size,
    };
  }

  private getPackageGroup(packageInstance: TicketPackageInstance): PackageGroup {
    const shareGroups = this.getShareGroups(packageInstance.Shares);

    return {
      packageInstances: [],
      name: packageInstance.TicketPackage.Name,
      shareGroups,
    };
  }
}
